import { type ReactNode, useCallback, useMemo } from 'react'
import { ToggleStatsColumns } from 'app/routes/stats/components'
import { type EntityId, isEmpty, pluralize, type ReportColumnDefinition, unique } from '@terros/common'
import { useToastError } from '@hooks'
import { useReportColumns, useSafeCurrentWorkflow, useUpdatePreference } from '@front/model'
import { PreferenceInput } from './PreferenceInput'

type PreferenceColumnsKeys = 'leaderboardColumns' | 'defaultStatsColumns'

type Props = {
  targetId: EntityId
  disabled?: boolean
  isDeprecated?: boolean
  preference: PreferenceColumnsKeys
}

export const PreferenceColumnsSelect = ({ targetId, disabled, preference }: Props): ReactNode => {
  const { update, error: updateError } = useUpdatePreference(preference, targetId)
  useToastError(updateError)

  const setDefault = useCallback(async () => {
    await update(undefined)
  }, [update])

  const workflowId = useSafeCurrentWorkflow()?.workflow.id
  const { data: options, isLoading: isLoadingReportColumns } = useReportColumns(workflowId)
  const validColumns = useMemo(() => new Set(options?.map((option) => option.value)), [options])

  const handleUpdate = useCallback(
    async (value: string[]) => {
      if (isEmpty(value)) {
        await setDefault()
        return
      }

      await update(unique(value.filter((column) => validColumns.has(column))))
    },
    [setDefault, update, validColumns]
  )

  return (
    <PreferenceInput targetId={targetId} preference={preference} isLoading={isLoadingReportColumns}>
      {({ data, disabled: isReadOnly }) => {
        const selectedColumns = data?.current ?? data?.inherited ?? []
        return (
          <div>
            <ToggleStatsColumns
              changeOnSubmit
              disabled={disabled || isReadOnly}
              label={toSelectedColumnsLabel(selectedColumns, options)}
              selectedColumns={selectedColumns}
              setSelectedColumns={handleUpdate}
            />
          </div>
        )
      }}
    </PreferenceInput>
  )
}

const toSelectedColumnsLabel = (
  selectedColumns: string[],
  allOptions: ReportColumnDefinition[] | undefined
): string => {
  if (isEmpty(selectedColumns) || !allOptions) return 'No metrics selected'
  const selectedColumnsDefs = allOptions.filter((option) => selectedColumns.includes(option.value))
  return `${selectedColumnsDefs.length} ${pluralize(selectedColumnsDefs.length, 'metric')} selected`
}
